import React, { FC, useState } from 'react'
import Grid from '@mui/material/Grid'
import { useTranslation } from 'react-i18next'
import { Alert } from '@carsys/ui'
import { Dropdown, Form, TextField } from '@carsys/form'
import * as Yup from 'yup'
import { DontKnowLoginDetailsDialog } from '../DontKnowLoginDetailsDialog'
import { UseWholesalerValidatorResult, useWholesalerValidatorBuilder } from 'app/auth/components/hooks/use-wholesaler-validator-builder'
import { Locales } from '@carsys/enums/locales'
import { IShopWholesaler } from '@cbgms/api'
import { BaseStepLayout } from '../../BaseStepLayout'
import { defaultStepFormProps } from '../../types.d'
import { StepNavigation } from '../../StepNavigation'
import { useSelector } from 'react-redux'
import { IRootReducerState } from 'state/rootReducer'
import { enumToLanguageOptions, supportedLanguagesPerEnvironment } from '@cbgms/base/translate/locales'
import { setTempLocale } from 'app/auth/state/actions'
import { WithRunInBackgroundHelper } from '../../use-should-run-in-background'
import { useApplicationName } from 'hooks/use-application-name'
import { isVPOMAXBE } from 'utils/Ordersystems/utils'
import { Box } from '@mui/material'

export const OnboardingCredentialsFormMijnGrossier: FC<
  defaultStepFormProps & {
    basePath: string
    onSubmit: (result: UseWholesalerValidatorResult) => void
    wholesaler: IShopWholesaler
  }
> = ({
  basePath,
  onSubmit,
  wholesaler,
  isFirstStep,
  prevStep,
  isPersisting,
  onSubmitSuccess,
  hideSkip,
  shouldRunInBackground,
  initialData,
  duplicationCheck
}) => {
  const { t, i18n } = useTranslation()
  const [initialLocale] = useState(i18n.resolvedLanguage)
  const environment = useSelector((state: IRootReducerState) => state.environment.Environment)
  const supportedLanguagesForCurrentEnvironment = supportedLanguagesPerEnvironment[environment]
  const localeOptions = enumToLanguageOptions(supportedLanguagesForCurrentEnvironment)

  const requiredMessage = t('global.field_is_required')
  const { appName } = useApplicationName()
  const systemName = t('Enum.EnumOrderSystem_MijnGrossier')
  const validationSchema = Yup.object().shape({
    DebtorNumber: Yup.string().required(requiredMessage),
    Password: Yup.string().required(requiredMessage)
  })

  const submitHandler = useWholesalerValidatorBuilder(
    basePath,
    i18n.resolvedLanguage || 'en-GB',
    wholesaler?.UUID || '',
    duplicationCheck,
    {
      onSuccess: result => {
        onSubmit(result)
      }
    }
  )

  const setNewLocale = async (locale: Locales) => {
    await i18n.changeLanguage(locale)
    setTempLocale(locale)
  }

  const onLocaleChange = (e?: React.ChangeEvent<any>) => {
    e?.target?.value && setNewLocale(e?.target?.value)
  }

  return (
    <BaseStepLayout title={t('Auth.OnboardingTitle')} subTitles={[t('Auth.OnboardingMessage')]}>
      <Form
        enableReinitialize
        NavigationConfirmation={null}
        initialValues={{
          ...initialData,
          Locale: (initialData as any).Locale || initialLocale,
          Password: (initialData as any).Password || '',
          // it's called debtor number for us, we don't want the external system inject it like that
          DebtorNumber: (initialData as any).Username || ''
        }}
        onSubmit={async values => {
          const wholesalerCredentials = {
            // UserID is used for validation inside the mutateAsync of sumbitHandler.
            UserId: values.DebtorNumber,
            Password: values.Password
          }

          await submitHandler.mutateAsync(wholesalerCredentials)
          return values
        }}
        onSubmitSuccess={onSubmitSuccess}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, handleSubmit }) => (
          <WithRunInBackgroundHelper enabled={shouldRunInBackground} isSubmitting={isSubmitting} onSubmit={handleSubmit}>
            <Grid container>
              {submitHandler.isError && (
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Alert square severity='error' title={t('global.error')} sx={{ whiteSpace: 'pre-line' }}>
                    {t(`Error.API.${submitHandler.error.response.data.Error}`, { appName, domain: window.location.hostname })}
                    {isVPOMAXBE(wholesaler) && (
                      <Box sx={{ whiteSpace: 'pre-line' }}>{t(`Auth.Onboarding.axbe-forgot-password`, { systemName })}</Box>
                    )}
                  </Alert>
                </Grid>
              )}
              <Grid item container spacing={2} xs={12} lg={6}>
                <Grid item xs={12}>
                  {localeOptions.length > 1 && (
                    <Dropdown name='Locale' onChange={onLocaleChange} label={t('Auth.SelectLanguage')} options={localeOptions} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField required name='DebtorNumber' label={t('Auth.DebtorNumber')} fullWidth autoFocus />
                </Grid>
                <Grid item xs={12}>
                  <TextField required name='Password' type='password' label={t('Auth.Password')} fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <DontKnowLoginDetailsDialog wholesaler={wholesaler} />
                </Grid>
              </Grid>
            </Grid>
            <StepNavigation
              prevStep={prevStep}
              hideBack={isFirstStep}
              isLoading={submitHandler.isLoading || isPersisting}
              hideSkip={hideSkip}
            />
          </WithRunInBackgroundHelper>
        )}
      </Form>
    </BaseStepLayout>
  )
}
